/**
 * A plastered gallery corridor: the connective tissue of the route. Pilaster
 * rhythm, laylight strip, and one gallery-wall cluster per bay between the
 * pilasters — the approach to a wing is hung in that wing's own saves, in the
 * palest wash of its colour. Exemplar for `role: 'route'` types.
 */

import { PALACE } from '../constants';
import { faceSegment } from '../hang';

const PIL_W = 0.35;
const PIL_D = 0.22;
const PIL_STEP = 4;
const BAY_CLEAR = PIL_W / 2 + 0.22; // air kept between a pilaster and a frame

// Where the pilasters land along the corridor's long faces. Shared by
// segments() and build() so no frame is ever hung onto a pilaster.
const rhythm = (node) => {
  const { WALL_T } = PALACE;
  const along = node.x2 - node.x1 >= node.z2 - node.z1; // long axis is x?
  const a1 = (along ? node.x1 : node.z1) + WALL_T + 1.2;
  const a2 = (along ? node.x2 : node.z2) - WALL_T - 1.2;
  const axes = [];
  for (let a = a1; a <= a2; a += PIL_STEP) axes.push(a);
  return { along, a1, a2, axes };
};

const corridor = {
  id: 'corridor',
  role: 'route',
  weight: 3,

  plan(rand, { axis }) {
    const long = 11 + rand() * 4;
    const cross = 5.4 + rand() * 0.8;
    return {
      w: axis === 'x' ? long : cross,
      d: axis === 'x' ? cross : long,
      height: 7.5,
    };
  },

  // One cluster per bay: the wall between two pilasters is exactly the panel a
  // gallery wall wants, and the rhythm does the composition for us.
  segments(node) {
    const { WALL_T, DOOR_W } = PALACE;
    const { along, axes } = rhythm(node);
    const lo = (along ? node.x1 : node.z1) + WALL_T + 0.5;
    const hi = (along ? node.x2 : node.z2) - WALL_T - 0.5;
    const edges = [lo, ...axes.filter((a) => a > lo && a < hi), hi];
    const segments = [];
    for (const side of along ? ['N', 'S'] : ['E', 'W']) {
      for (let i = 0; i + 1 < edges.length; i++) {
        const a = edges[i] + (i === 0 ? 0 : BAY_CLEAR);
        const b = edges[i + 1] - (i + 2 === edges.length ? 0 : BAY_CLEAR);
        if (b - a < 1.4) continue;
        const blocked = (node.doors || []).some((d) => {
          const half = (d.w || DOOR_W) / 2 + 0.5;
          return d.side === side && d.at + half > a && d.at - half < b;
        });
        if (!blocked) segments.push(faceSegment(node, side, a, b, { style: 'cluster' }));
      }
    }
    return segments;
  },

  build(node, T) {
    const { WALL_T, DOOR_W } = T.PALACE;
    const height = node.height;
    const { along, a1, a2 } = rhythm(node);
    const layLong = (along ? node.x2 - node.x1 : node.z2 - node.z1) - 2 * WALL_T - 2;
    const layCross = 2.2;
    const { group, colliders } = T.standardShell(node, T.kit, {
      wallMaterial: T.kit.wallFor(node.palette || 0, true),
      floorMaterial: T.kit.materials.marble,
      laylight: { w: along ? layLong : layCross, d: along ? layCross : layLong },
    });

    // Pilasters down both long faces, skipping past doors.
    const trim = [];
    for (let a = a1; a <= a2; a += PIL_STEP) {
      for (const s of [1, -1]) {
        const face = along
          ? (s > 0 ? node.z2 - WALL_T : node.z1 + WALL_T)
          : (s > 0 ? node.x2 - WALL_T : node.x1 + WALL_T);
        const blocked = (node.doors || []).some((d0) => {
          const doorFaceSide = along ? (s > 0 ? 'N' : 'S') : (s > 0 ? 'E' : 'W');
          return d0.side === doorFaceSide && Math.abs(d0.at - a) < (d0.w || DOOR_W) / 2 + 0.7;
        });
        if (blocked) continue;
        const f1 = face;
        const f2 = face - s * PIL_D;
        const capF2 = face - s * (PIL_D + 0.06);
        if (along) {
          trim.push(
            T.box(a - PIL_W / 2, a + PIL_W / 2, 0, height - 0.55, f1, f2),
            T.box(a - PIL_W / 2 - 0.06, a + PIL_W / 2 + 0.06, height - 0.55, height - 0.4, f1, capF2)
          );
        } else {
          trim.push(
            T.box(f1, f2, 0, height - 0.55, a - PIL_W / 2, a + PIL_W / 2),
            T.box(f1, capF2, height - 0.55, height - 0.4, a - PIL_W / 2 - 0.06, a + PIL_W / 2 + 0.06)
          );
        }
      }
    }
    if (trim.length) group.add(T.mergedMesh(trim, T.kit.materials.trim));
    return { group, colliders };
  },
};

export default corridor;
